/**
 * IrisChat — the functional IRIS Assistant chat surface, decoupled from any host.
 *
 * Wraps the Foundry AIP-Assist sidebar shell + header + footer with real
 * /api/iris/chat wiring. Used both by the /aip-assist full-page route and
 * by the global IrisOverlay (the toggleable right-side panel exposed from
 * the AppHeader and the rail).
 *
 * CASE-BOUNDED ANSWERING (CAP 6): when a `knowledge` slice for the active case
 * is supplied, IRIS first tries `answerCaseQuery` — a deterministic, offline
 * responder that answers the spec example queries (people in reports, vehicles,
 * officers/reports, active CalECPA deadlines, "draft a warrant for [phone]")
 * from the case knowledge graph ALONE, citing the source documents. Only when
 * no case-bounded rule matches does it fall back to the Bedrock chat path. This
 * mirrors Foundry's AIP Assist answering from the bound Object Set with no
 * external retrieval and no predictions.
 *
 * Conversation state lives in component state for now — no persistence yet
 * (next slice: session storage keyed by user + case). The full message
 * history is sent each turn since Bedrock InvokeModel is stateless.
 */

import { useState, type ReactElement, type ReactNode } from "react";
import { Icon, Intent, Spinner, Tag } from "@blueprintjs/core";

import {
  AipAssistFooter,
  AipAssistHeader,
  AipAssistSidebarShell,
  MessageFeedbackBar,
  SuggestedQuestionsCarousel,
  SwitchMessage,
  type FeedbackRating,
} from "../aip-assist";
import { IrisDisclaimer } from "./iris-disclaimer";
import { IrisCitationTag } from "./iris-citation-tag";
import { api, ApiError, type SourceRef } from "../../lib/api";
import {
  answerCaseQuery,
  CASE_SEED_QUESTIONS,
  type CaseKnowledge,
} from "../../lib/iris-case-answers";

/** Generic fallback prompts when IRIS opens without a bound case. */
const GENERIC_SEED_QUESTIONS = [
  "Summarize the most recent uploads on this case.",
  "What persons of interest are most-mentioned across the evidence?",
  "Draft a quick timeline of events from the last 24 hours of intake.",
];

export interface IrisChatProps {
  /** Optional case anchor — passed to the backend as system-prompt context. */
  caseId?: string;
  caseTitle?: string;
  /**
   * The active case's knowledge graph slice. When present, IRIS answers the
   * spec example queries from it locally (case-bounded, no external lookups)
   * and only falls back to Bedrock for free-form questions.
   */
  knowledge?: CaseKnowledge;
  /** Header pills slot. Defaults to model + provider pills. */
  pills?: ReactNode;
  /** Called when the user clicks the header's close button. Omit to hide it. */
  onClose?: () => void;
}

interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
  pending?: boolean;
  error?: string;
  rating?: FeedbackRating;
  /** Set when the turn was answered from the case knowledge graph (CAP 6). */
  grounded?: boolean;
  /** Source-document citations resolved from the case data. */
  sourceRefs?: SourceRef[];
}

function nextId(): string {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const DEFAULT_PILLS = (
  <>
    <Tag minimal round>Claude Sonnet 4.6</Tag>
    <Tag minimal round intent={Intent.SUCCESS} icon="cloud">
      Bedrock
    </Tag>
  </>
);

export function IrisChat({
  caseId,
  caseTitle,
  knowledge,
  pills = DEFAULT_PILLS,
  onClose,
}: IrisChatProps): ReactElement {
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("IRIS Assistant");
  const [activeQ, setActiveQ] = useState(0);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [sending, setSending] = useState(false);

  // Seed the empty state with the spec example questions when a case is bound;
  // otherwise fall back to the generic prompts.
  const seedQuestions = knowledge ? CASE_SEED_QUESTIONS : GENERIC_SEED_QUESTIONS;

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userTurn: ChatTurn = { id: nextId(), role: "user", content: trimmed };
    const assistantPending: ChatTurn = {
      id: nextId(),
      role: "assistant",
      content: "",
      pending: true,
    };

    setTurns((prev) => [...prev, userTurn, assistantPending]);
    setDraft("");

    // CAP 6 — case-bounded pre-empt: answer from the knowledge graph alone when
    // a rule matches. No network, no Bedrock, no external retrieval.
    if (knowledge) {
      const grounded = answerCaseQuery(trimmed, knowledge);
      if (grounded) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantPending.id
              ? {
                  ...t,
                  pending: false,
                  content: grounded.reply,
                  grounded: true,
                  sourceRefs: grounded.sourceRefs,
                }
              : t,
          ),
        );
        return;
      }
    }

    setSending(true);

    try {
      const history = [...turns, userTurn].map(({ role, content }) => ({ role, content }));
      const res = await api.chatIris({
        messages: history,
        caseId,
        caseTitle,
      });
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantPending.id ? { ...t, pending: false, content: res.reply } : t,
        ),
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantPending.id ? { ...t, pending: false, error: msg, content: "" } : t,
        ),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <AipAssistSidebarShell data-testid="iris-chat"
      header={
        <AipAssistHeader
          pills={pills}
          onNewChat={() => setTurns([])}
          onShowAllChats={() => undefined}
          onClose={onClose ?? (() => undefined)}
        />
      }
      footer={
        <AipAssistFooter
          value={draft}
          onValueChange={setDraft}
          onSubmit={() => submit(draft)}
          mode={mode}
          onPickMode={() => setMode(mode === "IRIS Assistant" ? "Developer Assist" : "IRIS Assistant")}
        />
      }
    >
      {turns.length === 0 && (
        <div className="bp-iris-empty" data-testid="iris-empty">
          <SwitchMessage avatar={<Icon icon="predictive-analysis" />} agentName="IRIS Assistant" />
          {knowledge ? (
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
              Ask about{caseTitle ? ` ${caseTitle}` : " this case"}. IRIS answers from the case
              knowledge graph — persons, evidence, warrants, surveillance and gap flags — and cites
              the source documents it draws from.
            </div>
          ) : (
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
              Ask anything about your cases, evidence, or workflow. IRIS Assistant cites the evidence
              items it draws from; every response is auditable.
            </div>
          )}
          <IrisDisclaimer>
            Answers are bounded to this case; no external lookups or predictions.
          </IrisDisclaimer>
          <SuggestedQuestionsCarousel
            questions={seedQuestions}
            activeIndex={activeQ}
            onPrev={() => setActiveQ((i) => Math.max(0, i - 1))}
            onNext={() => setActiveQ((i) => Math.min(seedQuestions.length - 1, i + 1))}
            onPick={(i) => {
              setActiveQ(i);
              submit(seedQuestions[i]);
            }}
          />
        </div>
      )}

      {turns.map((t, i) => (
        <ChatTurnView
          key={t.id}
          turn={t}
          onRate={(rating) =>
            setTurns((prev) => prev.map((x) => (x.id === t.id ? { ...x, rating } : x)))
          }
          isLast={i === turns.length - 1}
        />
      ))}
    </AipAssistSidebarShell>
  );
}

function ChatTurnView({
  turn,
  onRate,
  isLast,
}: {
  turn: ChatTurn;
  onRate: (rating: FeedbackRating) => void;
  isLast: boolean;
}): ReactElement {
  if (turn.role === "user") {
    return (
      <div className="bp-iris-sent-message" data-testid="iris-sent-message">
        <div className="bp-iris-sent-message-content">{turn.content}</div>
      </div>
    );
  }

  return (
    <div className="bp-iris-agent-message" data-testid="iris-agent-message">
      <div className="bp-iris-agent-message-author">
        <Icon icon="predictive-analysis" size={14} />
        <span>IRIS Assistant</span>
        {turn.grounded && (
          <Tag
            minimal
            round
            intent={Intent.PRIMARY}
            icon="graph"
            className="bp-iris-kg-pill"
            data-testid="iris-kg-pill"
          >
            Case knowledge graph
          </Tag>
        )}
      </div>
      <div className="bp-iris-agent-message-content bp-iris-answer-body">
        {turn.pending && (
          <div className="bp-iris-answer-pending">
            <Spinner size={14} />
            <span>Thinking…</span>
          </div>
        )}
        {turn.error && (
          <div className="bp-iris-answer-error">
            <Icon icon="error" size={14} /> {turn.error}
          </div>
        )}
        {!turn.pending && !turn.error && (
          <div className="bp-iris-answer-text">{turn.content}</div>
        )}
        {!turn.pending && !turn.error && turn.sourceRefs && turn.sourceRefs.length > 0 && (
          <div className="bp-iris-source-refs" data-testid="iris-source-refs">
            <span className="bp-iris-source-refs-label">Sources</span>
            <div className="bp-iris-source-refs-list">
              {turn.sourceRefs.map((ref, idx) => (
                <span
                  key={`${ref.evidenceId}-${idx}`}
                  className="bp-iris-source-ref"
                  data-testid="iris-source-ref"
                >
                  <IrisCitationTag number={idx + 1} />
                  <span className="bp-iris-source-ref-label">
                    {ref.label}
                    {ref.locator ? ` · ${ref.locator}` : ""}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {!turn.pending && !turn.error && isLast && (
        <MessageFeedbackBar rating={turn.rating} onRate={onRate} />
      )}
    </div>
  );
}
